import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  subscribeTopic as AmitySubscribeTopic,
  CommunityRepository,
  getCommunityTopic,
  PostRepository,
  SubscriptionLevels,
} from "@amityco/ts-sdk";
import { useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";

import { useAmityAuth, useUser } from "@/providers/context";

import { SEARCH_PARAM } from "../const";
import { CommunityGroupWithDetails, PostItem } from "../types";
import { mapAmityPost, updateMappedPost } from "../util";
import { useCommunityError } from "../hooks/useCommunityError";
import { usePostVisibility } from "../hooks/usePostVisibility";

const POST_PAGE_LIMIT = 6;

interface ContextValue {
  posts: PostItem[];
  pinnedPost: PostItem | null | undefined;
  loadingInitialContent: boolean;
  userBanned: boolean;
  loadingNextPage: boolean;
  hasNextPage: boolean;
  handleNextPage: () => void;
  handleJoinCommunity: () => void;
  handleLeaveCommunity: () => void;
  group: CommunityGroupWithDetails;
  registerPost: (postId: string, element: HTMLElement) => void;
  unregisterPost: (postId: string) => void;
  setClickedPostId: Dispatch<SetStateAction<string | null>>;
  restoreScrollToPost: () => void;
}

interface ProviderProps {
  group: CommunityGroupWithDetails;
  children: ReactNode | ReactNode[];
}

type OptionalContextValue = ContextValue | undefined;
const CommunityFeedContext = createContext<OptionalContextValue>(undefined);

const CommunityFeedProvider = ({ group, children }: ProviderProps) => {
  const { uid } = useUser();
  const { authState } = useAmityAuth();
  const queryClient = useQueryClient();
  const { handleGenericCommunityError } = useCommunityError();
  const { visiblePosts, registerPost, unregisterPost, observedElementsRef } =
    usePostVisibility();

  const [posts, setPosts] = useState<PostItem[]>([]);
  const [pinnedPostId, setPinnedPostId] = useState<string>();
  const [pinnedPost, setPinnedPost] = useState<PostItem | null>();
  const [hasNextPage, setHasNextPage] = useState(false);
  const [onNextPage, setOnNextPage] = useState<() => void>();
  const [loadingInitialContent, setLoadingInitialContent] = useState(true);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [userBanned, setUserBanned] = useState(false);
  const [clickedPostId, setClickedPostId] = useState<string | null>(null);
  const postsInitialized = useRef(false);
  const postsRef = useRef<PostItem[]>([]);
  const visiblePostsRef = useRef<Set<string>>(visiblePosts);

  const [searchParams] = useSearchParams();

  const query = searchParams.get(SEARCH_PARAM);

  const communityId = group.CommunityID;

  const unsubscribers = useRef<
    { unsub: Amity.Unsubscriber; subscribeTopic?: string }[]
  >([]);

  useEffect(() => {
    visiblePostsRef.current = visiblePosts;
  }, [visiblePosts]);

  useEffect(() => {
    const currentUnsubscribers = unsubscribers.current;
    return () => {
      currentUnsubscribers.forEach(({ unsub }) => {
        unsub();
      });
    };
  }, []);

  const onJoinChange = useCallback(async () => {
    // Invalidate the community groups query to refresh the list
    await queryClient.invalidateQueries(["community_groups", uid]);
  }, [queryClient, uid]);

  const handleJoinCommunity = useCallback(async () => {
    if (authState.state !== "success" || !authState.authenticatedUser) {
      return Promise.reject();
    }

    return new Promise((resolve, reject) => {
      CommunityRepository.joinCommunity(communityId)
        .then(async () => {
          await onJoinChange();
          resolve(true);
        })
        .catch((e) => {
          const errorMessage = e.toString();
          if (errorMessage.includes("User is banned from this community")) {
            setUserBanned(true);
          } else {
            handleGenericCommunityError(e);
          }
          reject(e);
        });
    });
  }, [communityId, authState, onJoinChange, handleGenericCommunityError]);

  const handleLeaveCommunity = useCallback(async () => {
    if (authState.state !== "success" || !authState.authenticatedUser) {
      return Promise.reject();
    }

    return new Promise((resolve, reject) => {
      CommunityRepository.leaveCommunity(communityId)
        .then(async () => {
          await onJoinChange();
          resolve(true);
        })
        .catch((e) => {
          handleGenericCommunityError(e);
          reject(e);
        });
    });
  }, [communityId, authState, onJoinChange, handleGenericCommunityError]);

  const subscribeToPost = (post: PostItem) => {
    const { subscribeTopic } = post;

    const alreadySubscribed = unsubscribers.current.some(
      ({ subscribeTopic: existingTopic }) => existingTopic === subscribeTopic,
    );

    if (!alreadySubscribed) {
      const unsub = AmitySubscribeTopic(subscribeTopic);
      unsubscribers.current.push({ unsub, subscribeTopic });
    }
  };

  useEffect(() => {
    postsInitialized.current = false;
    setPosts([]);
    postsRef.current = [];
    setLoadingInitialContent(true);
  }, [query]);

  const handleGetPosts: Amity.LiveCollectionCallback<Amity.Post> = useCallback(
    async (info) => {
      const { data, loading } = info;

      const currentPosts = postsRef.current;

      const getMappedPosts = data.map(async (rawPost) => {
        const existingMappedPost = currentPosts.find(
          (p) => p.id === rawPost.postId,
        );

        // If a post has already been mapped, we only want to update it if it is on-screen
        // To avoid rate limiting issues
        if (existingMappedPost) {
          const shouldUpdate = visiblePostsRef.current.has(
            existingMappedPost.id,
          );
          if (!shouldUpdate) {
            return existingMappedPost;
          }

          const updated = await updateMappedPost(existingMappedPost, rawPost);
          return updated;
        }

        const mappedPost = await mapAmityPost({
          post: rawPost,
          currentUserId: uid,
        });

        subscribeToPost(mappedPost);
        return mappedPost;
      });

      const mappedPosts = await Promise.all(getMappedPosts);

      setPosts(mappedPosts);
      postsRef.current = mappedPosts;
      setHasNextPage(info.hasNextPage ?? false);
      setOnNextPage(() => info.onNextPage);
      if (loading === false) {
        setLoadingInitialContent(false);
        setLoadingNextPage(false);
      }
    },
    [
      uid,
      setPosts,
      setHasNextPage,
      setOnNextPage,
      setLoadingInitialContent,
      setLoadingNextPage,
    ],
  );

  const handleNextPage = useCallback(() => {
    if (hasNextPage && onNextPage) {
      setLoadingNextPage(true);
      onNextPage();
    }
  }, [hasNextPage, onNextPage, setLoadingNextPage]);

  const restoreScrollToPost = useCallback(() => {
    if (!clickedPostId) {
      return;
    }

    const postElement = observedElementsRef.current?.get(clickedPostId);
    const scrollContainer = document.getElementById(
      "ScrollContainer--CommunityFeed",
    );

    if (postElement && scrollContainer) {
      // Use requestAnimationFrame to ensure the DOM is ready
      requestAnimationFrame(() => {
        const elementRect = postElement.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const relativeTop = elementRect.top - containerRect.top;
        scrollContainer.scrollTop = scrollContainer.scrollTop + relativeTop;

        setClickedPostId(null);
      });
    } else if (scrollContainer) {
      setClickedPostId(null);
    }
  }, [clickedPostId, observedElementsRef]);

  const getPosts = useCallback(() => {
    if (query) {
      const unsub = PostRepository.semanticSearchPosts(
        {
          query,
          targetId: communityId,
          targetType: "community",
          limit: POST_PAGE_LIMIT,
        },
        handleGetPosts,
      );

      return () => {
        unsub();
      };
    }

    const unsub = PostRepository.getPosts(
      {
        targetId: communityId,
        targetType: "community",
        limit: POST_PAGE_LIMIT,
      },
      handleGetPosts,
    );
    return () => {
      unsub();
    };
  }, [handleGetPosts, query, communityId]);

  // Subscribe to the community to get real-time post updates
  // Also get the pinPostID metadata to set the pinned post
  useEffect(() => {
    if (authState.authenticatedUser && uid) {
      const unsub = CommunityRepository.getCommunity(
        communityId,
        async ({ data }) => {
          if (data && data.metadata) {
            const topic = getCommunityTopic(data, SubscriptionLevels.POST);
            const unsubscribe = AmitySubscribeTopic(topic);
            unsubscribers.current.push({
              unsub: unsubscribe,
              subscribeTopic: topic,
            });

            const pinPostID = data.metadata.pinPostID;
            setPinnedPostId(pinPostID);
          }
        },
      );
      unsubscribers.current.push({ unsub });
    }
  }, [communityId, uid, authState.authenticatedUser]);

  // Update the pinned post when the pinnedPostId changes
  useEffect(() => {
    const updatePinnedPost = (id: string) => {
      const unsub = PostRepository.getPost(id, async ({ data }) => {
        if (data) {
          const mappedPinnedPost = await mapAmityPost({
            post: data,
            currentUserId: uid,
          });
          const post = { ...mappedPinnedPost, pinned: true };
          setPinnedPost(post);
        }
      });
      unsubscribers.current.push({ unsub });
    };

    if (pinnedPostId) {
      updatePinnedPost(pinnedPostId);
    } else {
      setPinnedPost(null);
    }
  }, [pinnedPostId, uid]);

  // Once community is subscribed to and pinned post is set, get the posts to initialize the feed
  useEffect(() => {
    if (pinnedPost !== undefined && !postsInitialized.current) {
      postsInitialized.current = true;
      getPosts();
    }
  }, [pinnedPost, getPosts]);

  return (
    <CommunityFeedContext.Provider
      value={{
        posts,
        pinnedPost,
        loadingInitialContent,
        userBanned,
        loadingNextPage,
        hasNextPage,
        handleNextPage,
        handleJoinCommunity,
        handleLeaveCommunity,
        group,
        registerPost,
        unregisterPost,
        setClickedPostId,
        restoreScrollToPost,
      }}
    >
      {children}
    </CommunityFeedContext.Provider>
  );
};

export { CommunityFeedContext, CommunityFeedProvider };
